import React, { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import {
  StaffListItem,
  StaffFilters,
} from "../../types/staff.types";
import { ClinicStaffService } from "../../services/api/clinic.staff.service";
import { ClinicService } from "../../services/api/clinic.service";
import { StaffFiltersBar } from "./StaffFiltersBar";
import { StaffListingTable } from "./StaffListingTable";
import { StaffDetailsComponent } from "./StaffDetailsComponent";
import { StaffOnboardingModal } from "../../shared/StaffModals/StaffOnboardingModal";
import { StaffStatusModal } from "../../shared/StaffModals/StaffStatusModal";
import { StaffDocumentUploadModal } from "../../shared/StaffModals/StaffDocumentUploadModal";

export const AdminStaffPage: React.FC = () => {
  const [staffList, setStaffList] = useState<StaffListItem[]>([]);
  const [clinicsList, setClinicsList] = useState<{ id: string; name: string }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItemsCount, setTotalItemsCount] = useState(0);

  const [filters, setFilters] = useState<StaffFilters>({
    searchText: "",
    department: "",
    employeeStatus: "",
    employmentType: "",
    clinicId: "",
  });

  // Details in-page state
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedStaffForStatus, setSelectedStaffForStatus] = useState<StaffListItem | null>(null);
  const [selectedStaffForDocUpload, setSelectedStaffForDocUpload] = useState<StaffListItem | null>(null);

  useEffect(() => {
    fetchClinics();
  }, []);

  useEffect(() => {
    fetchStaffList();
  }, [currentPage, pageSize, filters]);

  const fetchClinics = async () => {
    try {
      const res = await ClinicService.getClinics({ page: 1, pageSize: 100, searchText: "" });
      if (res.success && res.data?.dataList) {
        setClinicsList(
          res.data.dataList.map((c: any) => ({
            id: c.id || c.clinicId,
            name: c.name || c.clinicName,
          }))
        );
      }
    } catch (err) {
      console.error("Error fetching clinics in admin staff page:", err);
    }
  };

  const fetchStaffList = async () => {
    try {
      setIsLoading(true);
      const res = await ClinicStaffService.getStaffListing({
        page: currentPage,
        pageSize,
        clinicId: filters.clinicId || undefined,
        department: filters.department || undefined,
        designation: filters.designation || undefined,
        employeeStatus: filters.employeeStatus || undefined,
        employmentType: filters.employmentType || undefined,
        searchText: filters.searchText || undefined,
      });

      if (res.success && res.data) {
        setStaffList(res.data);
        if (res.pagination) {
          setTotalPages(res.pagination.totalPages || 1);
          setTotalItemsCount(res.pagination.totalItemsCount || res.data.length);
        }
      } else {
        setStaffList([]);
      }
    } catch (error) {
      console.error("Error fetching admin staff list:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetFilters = () => {
    setFilters({
      searchText: "",
      department: "",
      employeeStatus: "",
      employmentType: "",
      clinicId: "",
    });
    setCurrentPage(1);
  };

  const handleViewStaff = (staff: StaffListItem) => {
    setSelectedStaffId(staff.id);
    setIsUpdateMode(false);
  };

  const handleEditStaff = (staff: StaffListItem) => {
    setSelectedStaffId(staff.id);
    setIsUpdateMode(true);
  };

  const handleCloseDetails = () => {
    setSelectedStaffId(null);
    setIsUpdateMode(false);
    fetchStaffList();
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      {selectedStaffId ? (
        /* In-page Admin Staff Details & Edit View */
        <StaffDetailsComponent
          staffId={selectedStaffId}
          isUpdate={isUpdateMode}
          readOnly={false}
          onClose={handleCloseDetails}
        />
      ) : (
        /* Staff Listing Table View */
        <>
          {/* Top Header & Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Staff Management</h1>
              <p className="text-xs text-gray-500 mt-0.5">
                Add, manage, and monitor clinic staff members and credentials across clinics
              </p>
            </div>

            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-xl hover:bg-emerald-700 transition-colors shadow-sm whitespace-nowrap"
            >
              <Plus className="w-4 h-4" />
              Add Staff
            </button>
          </div>

          {/* Filters Bar */}
          <StaffFiltersBar
            filters={filters}
            clinics={clinicsList}
            onFilterChange={(f) => {
              setFilters(f);
              setCurrentPage(1);
            }}
            onReset={handleResetFilters}
          />

          {/* Table Listing */}
          <StaffListingTable
            staffList={staffList}
            isLoading={isLoading}
            currentPage={currentPage}
            pageSize={pageSize}
            totalItemsCount={totalItemsCount}
            totalPages={totalPages}
            readOnly={false}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            onViewDetails={handleViewStaff}
            onEditDetails={handleEditStaff}
            onUpdateStatus={(staff) => setSelectedStaffForStatus(staff)}
            onUploadDoc={(staff) => setSelectedStaffForDocUpload(staff)}
          />
        </>
      )}

      {/* Onboarding Modal */}
      <StaffOnboardingModal
        isOpen={showAddModal}
        clinicId={filters.clinicId || undefined}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => fetchStaffList()}
      />

      {/* Update Status Modal */}
      {selectedStaffForStatus && (
        <StaffStatusModal
          isOpen={Boolean(selectedStaffForStatus)}
          staffId={selectedStaffForStatus.id}
          staffName={selectedStaffForStatus.fullName}
          currentStatus={selectedStaffForStatus.employeeStatus}
          onClose={() => setSelectedStaffForStatus(null)}
          onSuccess={() => fetchStaffList()}
        />
      )}

      {/* Document Upload Modal */}
      {selectedStaffForDocUpload && (
        <StaffDocumentUploadModal
          isOpen={Boolean(selectedStaffForDocUpload)}
          staffId={selectedStaffForDocUpload.id}
          staffName={selectedStaffForDocUpload.fullName}
          onClose={() => setSelectedStaffForDocUpload(null)}
          onSuccess={() => fetchStaffList()}
        />
      )}
    </div>
  );
};
